import { Fragment, memo, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import AmountContainer from "app/pages/.shared/AmountContainer";
import AmountSign from "app/pages/.shared/AmountSign/AmountSign";
import isEmpty from "lodash/isEmpty";
import { FormattedDate, FormattedMessage } from "react-intl";
import {
	MAX_PAYMENT_INSTALMENTS_WITH_FEES_DISPLAYED,
	TRANSPORTATION_TYPES,
	USABLE_CREDITS_STATUS,
	RESOLUTION,
} from "app/constants";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import CouponsCguPopoverButton from "app/pages/Booking/Payment/PaymentCoupons/CouponsCGUPopoverButton/CouponsCGUPopoverButton";
import { sendTagOnCGUCreditNoteClick } from "app/utils/analytics";
import AppGlobalsContext from "app/AppGlobalsContext";

import "./ExtraPriceDetails.scss";
import { checkIfActivityIncluded } from "app/utils/utils";
import { groupRooms } from "app/pages/Booking/Quotation/Accommodation/utils/accommodationUtils";

const ExtraPriceDetails = ({
	transfer = {},
	includedTransfer,
	insurance = {},
	board = {},
	flight = {},
	accommodation = {},
	totalUsableCredit,
	totalUsableCreditNote,
	promotionValue,
	promotionCode = "",
	coutCredit = 0,
	activities = [],
	usableCouponsStatus,
	isPagePayment = false,
	isPageQuote = false,
	selectedPaymentMode,
	specialOffer = {},
	creditsAmount = 0,
	prebookTotal = 0,
	isCreditEnabled = false,
	isCreditNoteEnabled = false,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const accommodationUpgradePrice = accommodation?.upgradePrice || 0;
	const boardUpgradePrice = board?.upgradePrice || 0;

	const handleOnCreditNoteCGUClick = useCallback(() => {
		sendTagOnCGUCreditNoteClick();
	}, []);

	let accommodationBoardNode = null;

	if (board.included && !accommodationUpgradePrice && !boardUpgradePrice) {
		// affichage des pensions incluses sans supplément de prix ni pour la chambre ni pour la pension
		accommodationBoardNode = (
			<div className="extra-price-detail__accommodation">
				<div className="extra-price-detail__item" data-testid="price-detail-room-board">
					<div className="extra-price-detail__label">{board?.label}</div>
					<div className="extra-price-detail__value">
						<FormattedMessage id="general.included" />
					</div>
				</div>
			</div>
		);
	} else if (accommodation.included && !board.included && boardUpgradePrice > 0) {
		// affichage des pensions non incluses avec supplément de prix pour la chambre incluse uniquement
		accommodationBoardNode = (
			<div className="extra-price-detail__accommodation">
				<div className="extra-price-detail__item" data-testid="price-detail-room-board">
					<div className="extra-price-detail__label">{board?.label}</div>
					<div className="extra-price-detail__value">
						<AmountContainer
							isBold={false}
							prefix={<AmountSign sign="+" />}
							amount={boardUpgradePrice}
						/>
					</div>
				</div>
			</div>
		);
	} else if (!accommodation.included && accommodationUpgradePrice + boardUpgradePrice > 0) {
		// affichage des pensions avec supplément de prix pour la chambre et/ou la pension
		const processedRooms = groupRooms(accommodation?.items);

		accommodationBoardNode = (
			<div className="extra-price-detail__accommodation">
				<div className="extra-price-detail__item" data-testid="price-detail-room-board">
					<div className="extra-price-detail__label">
						<FormattedMessage id="price.detail.room.board.upgrade.label" />
					</div>
					<div className="extra-price-detail__value">
						<AmountContainer
							isBold={false}
							prefix={<AmountSign sign="+" />}
							amount={accommodationUpgradePrice + boardUpgradePrice}
						/>
					</div>
				</div>
				<div
					className="extra-price-detail__item"
					data-testid="price-detail-room-board-details"
				>
					<div className="extra-price-detail__label">
						<ul className="extra-price-detail__details">
							{processedRooms?.map((item, index) => {
								return (
									<li key={index}>
										{item.quantity} x {item.label}
									</li>
								);
							})}
							<li>{board.label}</li>
						</ul>
					</div>
				</div>
			</div>
		);
	}

	const isTransportationTypeWithoutFlight = flight.type !== TRANSPORTATION_TYPES.FLIGHT;

	const transportPositiveUpgradePriceLabel = isTransportationTypeWithoutFlight ? (
		<FormattedMessage id="price.detail.transport.upgrade.label" />
	) : (
		<FormattedMessage id="price.detail.flight.upgrade.label" />
	);

	const transportNegativeUpgradePriceLabel = isTransportationTypeWithoutFlight ? (
		<FormattedMessage id="price.detail.transport.reduction.label" />
	) : (
		<FormattedMessage id="price.detail.flight.reduction.label" />
	);

	const flightNode = !isEmpty(flight) && !flight.included && flight.upgradePrice !== 0 && (
		<div className="extra-price-detail__item" data-testid="price-detail-transport">
			<div className="extra-price-detail__label">
				{flight.upgradePrice < 0
					? transportNegativeUpgradePriceLabel
					: transportPositiveUpgradePriceLabel}
			</div>
			<div className="extra-price-detail__value">
				<AmountContainer
					isBold={false}
					prefix={flight.upgradePrice < 0 ? "" : <AmountSign sign="+" />}
					amount={flight.upgradePrice || 0}
				/>
			</div>
		</div>
	);

	const hasIncludedTransfer = includedTransfer !== undefined;

	const transferNode = !isEmpty(transfer) && transfer.code !== "NO_TRANSFER" && (
		<div className="extra-price-detail__item" data-testid="price-detail-transfert">
			<div className="extra-price-detail__label">
				{hasIncludedTransfer && transfer.included ? transfer.vehicule : false}

				{hasIncludedTransfer && !transfer.included && (
					<FormattedMessage
						id="price.detail.transfer.upgrade.label"
						values={{
							transfer: transfer.vehicule,
						}}
					/>
				)}

				{!hasIncludedTransfer && transfer.price > 0 ? transfer.vehicule : false}
			</div>
			<div className="extra-price-detail__value">
				{hasIncludedTransfer && transfer.included && (
					<FormattedMessage id="general.included" />
				)}

				{hasIncludedTransfer && !transfer.included && (
					<AmountContainer
						isBold={false}
						prefix={<AmountSign sign="+" />}
						amount={transfer.upgradePrice}
					/>
				)}

				{!hasIncludedTransfer && transfer.price > 0 && (
					<AmountContainer
						isBold={false}
						prefix={<AmountSign sign="+" />}
						amount={transfer.price}
					/>
				)}
			</div>
		</div>
	);

	const insuranceNode = !isEmpty(insurance) && isPagePayment && insurance?.price > 0 && (
		<div className="extra-price-detail__item" data-testid="price-detail-insurance">
			<div className="extra-price-detail__label">{insurance.name}</div>
			<div className="extra-price-detail__value">
				<AmountContainer
					isBold={false}
					prefix={<AmountSign sign="+" />}
					amount={insurance.price}
				/>
			</div>
		</div>
	);

	/* TODO 4x A rendre générique */
	const coutCreditNode = coutCredit > 0 &&
		selectedPaymentMode <= MAX_PAYMENT_INSTALMENTS_WITH_FEES_DISPLAYED &&
		isPagePayment && (
			<div className="extra-price-detail__item" data-testid="price-detail-cout-credit">
				<div className="extra-price-detail__label">
					<FormattedMessage
						id="price.detail.cout.credit.label"
						values={{ terms: selectedPaymentMode }}
					/>
				</div>
				<div className="extra-price-detail__value">
					<AmountContainer
						isBold={false}
						prefix={<AmountSign sign="+" />}
						amount={coutCredit}
					/>
				</div>
			</div>
		);

	const promotion = promotionValue > 0 && isPagePayment && (
		<div className="extra-price-detail__item" data-testid="price-detail-promotion-code">
			<div className="extra-price-detail__label">
				<FormattedMessage id="savings.promo.code.label" values={{ code: promotionCode }} />
			</div>
			<div className="extra-price-detail__value">
				<AmountContainer
					isBold={false}
					prefix={<AmountSign sign="-" />}
					amount={promotionValue}
				/>
			</div>
		</div>
	);

	const isCalculatingPrices =
		usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_CREDIT_NOTES ||
		usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_COUPONS;

	let credits;
	if (isPagePayment) {
		credits = (totalUsableCredit > 0 || isCalculatingPrices) && (
			<div className="extra-price-detail__item" data-testid="price-detail-credits">
				<div className="extra-price-detail__label extra-price-detail__label--info">
					<FormattedMessage id="price.detail.credits.label" />
					<CouponsCguPopoverButton
						position={isMobile ? "top" : "bottom"}
						hasCreditInfos={true}
						onClick={handleOnCreditNoteCGUClick}
					/>
				</div>
				<div className="extra-price-detail__value">
					{isCalculatingPrices ? (
						<LoaderBar width={80} />
					) : (
						<AmountContainer
							isBold={false}
							prefix={<AmountSign sign="-" />}
							amount={totalUsableCredit}
						/>
					)}
				</div>
			</div>
		);
	}
	if (isPageQuote) {
		const dixPercentOfPrebookTotal = (prebookTotal * 0.1).toFixed(1);
		credits = creditsAmount > 0 && (
			<div className="extra-price-detail__item" data-testid="price-detail-credits">
				<div className="extra-price-detail__label extra-price-detail__label--info">
					<FormattedMessage id="price.detail.credits.label" />
					<CouponsCguPopoverButton
						position={isMobile ? "top" : "bottom"}
						hasCreditInfos={true}
						onClick={handleOnCreditNoteCGUClick}
					/>
				</div>
				<div className="extra-price-detail__value">
					<AmountContainer
						isBold={false}
						prefix={<AmountSign sign="-" />}
						amount={
							creditsAmount <= dixPercentOfPrebookTotal
								? creditsAmount
								: dixPercentOfPrebookTotal
						}
					/>
				</div>
			</div>
		);
	}

	const creditNote = (totalUsableCreditNote > 0 || isCalculatingPrices) && isPagePayment && (
		<div className="extra-price-detail__item" data-testid="price-detail-creditnotes">
			<div className="extra-price-detail__label">
				<FormattedMessage id="price.detail.creditnotes.label" />
			</div>
			<div className="extra-price-detail__value">
				{isCalculatingPrices ? (
					<LoaderBar width={80} />
				) : (
					<AmountContainer
						isBold={false}
						prefix={<AmountSign sign="-" />}
						amount={totalUsableCreditNote}
					/>
				)}
			</div>
		</div>
	);

	const activityNode = activities.map(activity => {
		const total = activity.guests.reduce((acc, guest) => {
			const guestCount = guest.guestsCount;

			const guestPrices = guest.pricePerQuantity[guestCount] || {};
			const guestPrice = guestPrices.price || 0;
			const guestDiscount = guestPrices.discount || 0;

			return acc + guestPrice + guestDiscount;
		}, 0);

		const totalDiscount = activity.guests.reduce((acc, guest) => {
			const guestCount = guest.guestsCount;

			const guestPrices = guest.pricePerQuantity[guestCount] || {};
			const guestDiscount = guestPrices.discount || 0;

			return acc + guestDiscount;
		}, 0);

		const hasIncludedGuest = checkIfActivityIncluded(activity.guests);

		const removedGuests = activity.guests.reduce((acc, guest) => {
			const guestCount = guest.guestsCount;
			const includedGuests = guest.includedGuest;

			return acc + includedGuests - guestCount;
		}, 0);

		return (
			<div key={activity.code} className="extra-price-detail__activity">
				<div
					className="extra-price-detail__item"
					data-testid={`price-detail-activity-${activity.code}`}
				>
					<div className="extra-price-detail__label">
						<FormattedMessage
							id="price.detail.activity.label"
							values={{
								activityName: activity.name,
								activityDate: (
									<FormattedDate
										timeZone="UTC"
										month="short"
										day="numeric"
										weekday="short"
										value={activity.date}
									/>
								),
							}}
						/>
					</div>
					{!hasIncludedGuest && (
						<div className="extra-price-detail__value">
							<AmountContainer
								isBold={false}
								prefix={<AmountSign sign="+" />}
								amount={total}
							/>
						</div>
					)}
				</div>
				<ul
					className="extra-price-detail__details"
					data-testid={`price-detail-activity-${activity.code}-guests`}
				>
					{activity.guests.map((guest, index) => {
						const hasOnlyMinAge = guest.minAge && !guest.maxAge;
						const hasOnlyMaxAge = !guest.minAge && guest.maxAge;
						const hasMinAgeAndMaxAge = guest.minAge && guest.maxAge;
						const isForAllAges = !guest.minAge && !guest.maxAge;

						const guestCount = guest.guestsCount;

						const guestPrices = guest.pricePerQuantity[guestCount] || {};
						const guestPrice = guestPrices.price || 0;

						let guestLabel = "";

						if (hasOnlyMinAge) {
							guestLabel = (
								<Fragment>
									<FormattedMessage id="price.detail.activity.guests.label" />{" "}
									<FormattedMessage
										id="quotation.activity.guest.age.min.label"
										values={{
											minAge: guest.minAge,
										}}
									/>
								</Fragment>
							);
						} else if (hasMinAgeAndMaxAge) {
							guestLabel = (
								<Fragment>
									<FormattedMessage id="price.detail.activity.guests.label" />{" "}
									<FormattedMessage
										id="quotation.activity.guest.age.range.label"
										values={{
											minAge: guest.minAge,
											maxAge: guest.maxAge,
										}}
									/>
								</Fragment>
							);
						} else if (hasOnlyMaxAge) {
							guestLabel = (
								<Fragment>
									<FormattedMessage id="price.detail.activity.guests.label" />{" "}
									<FormattedMessage
										id="quotation.activity.guest.age.max.label"
										values={{
											maxAge: guest.maxAge,
										}}
									/>
								</Fragment>
							);
						} else if (isForAllAges) {
							guestLabel = (
								<FormattedMessage id="price.detail.activity.guests.label" />
							);
						}

						return (
							guestCount > 0 && (
								<li key={index} className="extra-price-detail__subitem">
									<div className="extra-price-detail__label">
										{guestCount} x {guestLabel}
									</div>
									{hasIncludedGuest && (
										<div className="extra-price-detail__value">
											{guestPrice > 0 ? (
												<AmountContainer
													isBold={false}
													prefix={<AmountSign sign="+" />}
													amount={guestPrice}
												/>
											) : (
												<FormattedMessage id="general.included" />
											)}
										</div>
									)}
								</li>
							)
						);
					})}

					{totalDiscount < 0 && hasIncludedGuest && (
						<li key="discount" className="extra-price-detail__subitem">
							<div className="extra-price-detail__label">
								{removedGuests} x{" "}
								<FormattedMessage id="price.detail.activity.discount.guests.label" />
							</div>
							<div className="extra-price-detail__value">
								<AmountContainer
									isBold={false}
									prefix={<AmountSign sign="-" />}
									amount={Math.abs(totalDiscount)}
								/>
							</div>
						</li>
					)}
				</ul>
			</div>
		);
	});

	const quotationNode = specialOffer.ui?.quotationItemLabel && (
		<div className="extra-price-detail__item">
			<div className="extra-price-detail__label">{specialOffer.ui?.quotationItemLabel}</div>
			<div className="extra-price-detail__value">
				<FormattedMessage id="general.included" />
			</div>
		</div>
	);

	if (
		!(
			accommodationBoardNode ||
			flightNode ||
			transferNode ||
			activities?.length > 0 ||
			promotion ||
			(isCreditEnabled && credits) ||
			insuranceNode ||
			(isCreditNoteEnabled && creditNote) ||
			coutCreditNode ||
			quotationNode
		)
	) {
		return null;
	}

	return (
		<div className="extra-price-detail">
			{accommodationBoardNode}
			{flightNode}
			{transferNode}
			{activityNode}
			{promotion}
			{isCreditEnabled && credits}
			{insuranceNode}
			{isCreditNoteEnabled && creditNote}
			{coutCreditNode}
			{quotationNode}
		</div>
	);
};

ExtraPriceDetails.propTypes = {
	coutCredit: PropTypes.number,
	board: PropTypes.object,
	accommodation: PropTypes.object,
	flight: PropTypes.object,
	insurance: PropTypes.object,
	transfer: PropTypes.object,
	includedTransfer: PropTypes.object,
	totalUsableCredit: PropTypes.number,
	totalUsableCreditNote: PropTypes.number,
	promotionValue: PropTypes.number,
	promotionCode: PropTypes.string,
	activities: PropTypes.arrayOf(PropTypes.object),
	usableCouponsStatus: PropTypes.string,
	isPagePayment: PropTypes.bool,
	isPageQuote: PropTypes.bool,
	selectedPaymentMode: PropTypes.number,
	specialOffer: PropTypes.object,
	creditsAmount: PropTypes.number,
	prebookTotal: PropTypes.number,
	isCreditEnabled: PropTypes.bool,
	isCreditNoteEnabled: PropTypes.bool,
};

export default memo(ExtraPriceDetails);
